import { CreateKnowledgeBaseContentSchema, EditKnowledgeBaseContentSchema } from 'validations/KnowledgeBaseContentSchema'
import { KnowledgeBaseVisibilityType } from 'types/KnowledgeBaseVisibilityType'
import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { AddKnowledgeBaseObjectVariables, VisibilityType } from 'api/mutations/knowledgeBase/types'
import { metadataEntriesToMetadataRecord } from 'screens/KnowledgeBase/utils'
import { Translation } from 'types/Translation'
import { KnowledgeBaseContentFormFields } from './types'

const commonDefaultValues = {
  name: '',
  nameTranslations: [],
  description: '',
  descriptionTranslations: [],
  destination: null,
  knowledgeBaseFilesIds: [],
  visibility: KnowledgeBaseVisibilityType.Company,
  image: null,
  labelIds: [],
}

const presentationTestDefaultValues = {
  difficulty: 3,
  metadata: [],
}

const languageCodeSorter = (a: Translation, b: Translation) => (
  a.languageCode > b.languageCode
    ? 1
    : (a.languageCode === b.languageCode ? 0 : -1)
)

const fillTranslations = (
  nameTranslations: Translation[] = [],
  descriptionTranslations: Translation[] = [],
  hasDescription = true
) => {
  if (!hasDescription) {
    return { nameTranslations, descriptionTranslations }
  }

  const nameTranslationsSet = new Set(nameTranslations.map((item) => item.languageCode))
  const descriptionTranslationsSet = new Set(
    descriptionTranslations.map((item) => item.languageCode)
  )

  const newNameTranslations = [...nameTranslations]
  const newDescriptionTranslations = [...descriptionTranslations]

  descriptionTranslationsSet.forEach((languageCode) => {
    if (!nameTranslationsSet.has(languageCode)) {
      newNameTranslations.push({ languageCode, value: '' })
    }
  })

  nameTranslationsSet.forEach((languageCode) => {
    if (!descriptionTranslationsSet.has(languageCode)) {
      newDescriptionTranslations.push({ languageCode, value: '' })
    }
  })

  return {
    nameTranslations: newNameTranslations.sort(languageCodeSorter),
    descriptionTranslations: newDescriptionTranslations.sort(languageCodeSorter),
  }
}

const getDefaultValues = (
  type: KnowledgeBaseContentType,
  initValues?: KnowledgeBaseContentFormFields
): KnowledgeBaseContentFormFields => {
  switch (type) {
    case KnowledgeBaseContentType.QUIZ:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: null,
        type: KnowledgeBaseContentType.QUIZ,
        subForm: {
          canGoBack: false,
          quizDuration: false,
          showAnswers: false,
          randomQuestionOrder: false,
          markWeight: 1,
          quantityOfRandomQuestions: null,
          difficulty: 3
        }
      }
    case KnowledgeBaseContentType.PRESENTATION:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: initValues?.duration || 60,
        type: KnowledgeBaseContentType.PRESENTATION,
        subForm: presentationTestDefaultValues
      }
    case KnowledgeBaseContentType.TEST:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: initValues?.duration || 60,
        type: KnowledgeBaseContentType.TEST,
        subForm: presentationTestDefaultValues
      }
    case KnowledgeBaseContentType.SANDBOX_TEST:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: initValues?.duration || 60,
        type: KnowledgeBaseContentType.SANDBOX_TEST,
        subForm: presentationTestDefaultValues
      }
    case KnowledgeBaseContentType.FILE:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: initValues?.duration || 60,
        type: KnowledgeBaseContentType.FILE,
        subForm: {}
      }
    default:
      return {
        ...commonDefaultValues,
        ...initValues,
        duration: initValues?.duration || 60,
        type: KnowledgeBaseContentType.TEST,
        subForm: presentationTestDefaultValues
      }
  }
}

const getValidationSchema = (isEditing: boolean) => (
  isEditing
    ? EditKnowledgeBaseContentSchema
    : CreateKnowledgeBaseContentSchema
)

const parseContentFields = (
  variables: KnowledgeBaseContentFormFields,
  languageCode: string,
  sendMetadata = true,
): AddKnowledgeBaseObjectVariables => {
  const {
    destination,
    type,
    visibility,
    duration,
    knowledgeBaseFilesIds,
    subForm,
    image,
    nameTranslations,
    descriptionTranslations,
    ...values
  } = variables

  return {
    ...values,
    languageCode,
    nameTranslations: nameTranslations.filter(({ value }) => value !== ''),
    descriptionTranslations: descriptionTranslations?.filter(({ value }) => value !== ''),
    knowledgeBaseFileIds: knowledgeBaseFilesIds,
    parentId: destination !== null ? destination.toString() : destination,
    dataType: type,
    visibility: visibility as VisibilityType,
    duration: duration && duration * 60,
    ...('metadata' in subForm
      ? {
        metadata: sendMetadata ? metadataEntriesToMetadataRecord(subForm.metadata) : null,
        difficulty: subForm.difficulty
      }
      : {
        ...Object.fromEntries(
          Object.entries(subForm).filter(([key]) => !['metadata'].includes(key))
        )
      }
    ),
  }
}

export {
  getDefaultValues,
  getValidationSchema,
  parseContentFields,
  fillTranslations,
  languageCodeSorter,
}
